import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Button,
  useTheme,
  useMediaQuery,
  Grid,
  CircularProgress,
  Alert,
} from '@mui/material';
import { WhatsApp as WhatsAppIcon, Facebook as FacebookIcon, Instagram as InstagramIcon, Forum as ForumIcon, Chat as ChatIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { convertFirestoreDate } from '../utils/dateUtils';
import { db } from '../firebase';
import { SocialGroup, SocialType } from '../types/social';
import PageLayout from '../components/PageLayout';
import PageTitle from '../components/PageTitle';

export const Communication = () => {
  const { isAdmin } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [groups, setGroups] = useState<SocialGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getSocialIcon = (type: SocialType) => {
    switch (type) {
      case 'whatsapp':
        return <WhatsAppIcon />;
      case 'facebook':
        return <FacebookIcon />;
      case 'discord':
        return <ForumIcon />;
      case 'instagram':
        return <InstagramIcon />;
      default:
        return null;
    }
  };

  useEffect(() => {
    // Écouter les changements dans la collection socials
    const q = query(
      collection(db, 'socials'),
      where('active', '==', true)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      try {
        const now = new Date();
        const groupsData = snapshot.docs
          .map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              createdAt: convertFirestoreDate(data.createdAt),
              updatedAt: convertFirestoreDate(data.updatedAt),
              expiresAt: convertFirestoreDate(data.expiresAt),
            } as SocialGroup;
          })
          .filter(group => {
            // Filtrer les groupes expirés
            if (group.expiresAt && group.expiresAt < now) {
              return false;
            }
            // Filtrer les groupes admin si l'utilisateur n'est pas admin
            if (group.forAdmin && !isAdmin) {
              return false;
            }
            return true;
          });
        setGroups(groupsData);
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors de la récupération des groupes:', err);
        setError("Une erreur s'est produite lors de la récupération des groupes.");
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [isAdmin]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      </Container>
    );
  }

  return (
    <PageLayout>
      <PageTitle
        title="Communication"
        icon={<ChatIcon />}
      />

      <Grid container spacing={3} justifyContent="center" sx={{ mt: 2 }}>
        {groups.map((group) => (
          <Grid item xs={12} sm={6} md={4} key={group.id}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                transition: 'none',
                '&:hover': {
                  transform: 'none',
                  boxShadow: 'none'
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  {getSocialIcon(group.type)}
                  <Typography variant="h6" component="div">
                    {group.name}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {group.description}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  href={group.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={getSocialIcon(group.type)}
                >
                  Rejoindre le groupe
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </PageLayout>
  );
};

export default Communication;
